import React, { useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { bindActionCreators } from 'redux'

import Dashboard from '../components/dashboard/Dashboard'
import Loading from '../components/shared/Loading/Loading'
import {
  extractMilestonesAnimationStatus,
  isDashboardLoading,
  isDashboardReady
} from '../util/dashboard.helpers'
import {
  getExchangeActivityChart,
  resetTransactionsChartState
} from '../store/actions/transactions'
import { getTokens, resetTokenStats } from '../store/actions/token'
import { calculateMemberMilestones } from '../data/members'
import { milestoneAnimationComplete } from '../store/actions/dashboard'
import { calculateExchangeInOutValue } from '../util/chart/transactionsGraph.helpers'

const DashboardContainer = ({
  dashboardSetupState,
  getExchangeActivityChart,
  getTokens,
  activePartnershipsCount,
  brandProfile,
  tokenProfile,
  tokenStatsProfile,
  milestoneAnimationComplete,
  resetTransactionsChartState,
  resetTokenStats,
  ...restProps
}) => {
  const { t } = useTranslation()

  const milestonesLatestData = (
    activePartnershipsCount,
    tokenProfile,
    tokenStatsProfile
  ) => {
    return calculateMemberMilestones(
      activePartnershipsCount,
      tokenStatsProfile.exchangeTxCount,
      calculateExchangeInOutValue(tokenStatsProfile, tokenProfile),
      t
    )
  }

  const isDashboardSetupReady = isDashboardReady(dashboardSetupState)

  useEffect(() => {
    getTokens()
  }, [])

  useEffect(() => {
    return () => {
      const milestonesAnimationStatus = extractMilestonesAnimationStatus(
        milestonesLatestData(
          activePartnershipsCount,
          tokenProfile,
          tokenStatsProfile
        )
      )
      if (Object.keys(milestonesAnimationStatus).length > 0) {
        milestoneAnimationComplete(milestonesAnimationStatus)
      }
    }
  }, [tokenProfile, tokenStatsProfile, activePartnershipsCount])

  useEffect(() => {
    if (isDashboardSetupReady) {
      getExchangeActivityChart()
    } else {
      resetTransactionsChartState()
      resetTokenStats()
    }
  }, [isDashboardSetupReady, brandProfile])

  const handleCompleteMilestoneAnimation = (key) => {
    milestoneAnimationComplete({
      [key]: true
    })
  }

  const membershipMilestonesData = useMemo(() => {
    return milestonesLatestData(
      activePartnershipsCount,
      tokenProfile,
      tokenStatsProfile
    )
  }, [tokenProfile, tokenStatsProfile, activePartnershipsCount])

  return isDashboardLoading(dashboardSetupState) ? (
    <Loading />
  ) : (
    <Dashboard
      t={t}
      dashboardSetupState={dashboardSetupState}
      activePartnershipsCount={activePartnershipsCount}
      membershipMilestonesData={membershipMilestonesData}
      tokenProfile={tokenProfile}
      isDashboardSetupReady={isDashboardSetupReady}
      onCompleteMilestoneAnimation={handleCompleteMilestoneAnimation}
      brandProfile={brandProfile}
      exchangeTxCount={tokenStatsProfile.exchangeTxCount}
      {...restProps}
    />
  )
}

const mapStateToProps = ({
  dashboardReducer,
  tokensReducer,
  transactionsReducer,
  mainReducer
}) => {
  const { billingProfile, mainProfile } = mainReducer
  const { dashboardSetupProfile, brandProfile } = dashboardReducer
  const { tokenProfile, tokenStatsProfile } = tokensReducer
  const { transactionsGraphProfile } = transactionsReducer
  const { dashboardSetupState, milestoneAnimationStatus } =
    dashboardSetupProfile
  return {
    dashboardSetupState,
    milestoneAnimationStatus,
    tokenProfile,
    tokenStatsProfile,
    brandProfile,
    transactionsGraphProfile,
    activePartnershipsCount: mainProfile.exchangePartnersStats.activeCount,
    billingProfile
  }
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getExchangeActivityChart,
      getTokens,
      milestoneAnimationComplete,
      resetTransactionsChartState,
      resetTokenStats
    },
    dispatch
  )

export default connect(mapStateToProps, mapDispatchToProps)(DashboardContainer)
